import { useState, type FormEvent } from 'react';
import { uploadNoticeFile } from '../api/documents';
import { useAuth } from '../auth/AuthProvider';

type Document = { id: string; kind: string; status: string; fileName: string };

/**
 * Before PayRight starts, the customer can attach their copy of the job's notice of commencement, usually a photo of the
 * one posted at the site. Research reads it first: it says which recorded notice to look for, and where a county's records
 * site cannot be searched automatically it is the one way PayRight can read the notice at all.
 */
export function CommencementCopy({ projectId, orderId, documents, onChange }: { projectId: string; orderId: string; documents: Document[]; onChange: () => void }) {
  const { token } = useAuth();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const attached = documents.find(document => document.kind === 'NOTICE_OF_COMMENCEMENT' && document.status === 'FINAL');

  async function upload(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = event.currentTarget;
    if (!token || !file?.size) return;
    setSaving(true); setError('');
    try { await uploadNoticeFile({ token, projectId, orderId, file, kind: 'NOTICE_OF_COMMENCEMENT' }); form.reset(); setFile(null); onChange(); }
    catch (reason) { setError((reason as Error).message); } finally { setSaving(false); }
  }

  return <section className="workspace-section" aria-label="Your copy of the notice of commencement">
    <div className="section-heading"><div><h2>Your copy of the notice of commencement</h2>
      {attached
        ? <p>You attached {attached.fileName}. PayRight reads it first, then looks for the recorded copy in the county’s records.</p>
        : <p>Optional, but it helps. If the notice of commencement is posted at the job site, a photo of it tells PayRight exactly which recorded notice to look for. Some counties’ records sites cannot be searched by PayRight, and there your copy is how PayRight reads the notice.</p>}
    </div></div>
    {!attached && <>
        <form className="stack settings-form" onSubmit={event => void upload(event)}>
          <label>Photo or PDF of the notice<input required name="file" type="file" accept="application/pdf,image/png,image/jpeg"
            onChange={event => setFile(event.target.files?.[0] ?? null)} /></label>
          {error && <p className="alert error">{error}</p>}
          <button className="button button-quiet" disabled={saving}>{saving ? 'Attaching…' : 'Attach your copy'}</button>
        </form>
      </>}
  </section>;
}
